import { z } from "zod"

import { INCH_UOM_ID } from "../constants"

import { numberSchemas } from "@/schemas/numberSchemas"
import type { ParsedProductFreights } from "@/screens/Products/types"

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createPackagingInfoSchema({
  validFreightItems,
  validFreightClasses,
  freightPairs,
}: ParsedProductFreights) {
  return z
    .object({
      freightItem: z.preprocess(
        (val) => (val === "" ? null : val),
        z
          .string()
          .regex(/^\d+$/, "Must be a number")
          .refine((val) => !val || validFreightItems.has(val), "Invalid Freight Item")
          .nullable()
      ),
      freightClass: z.preprocess(
        (val) => (val === "" ? null : val),
        z
          .string()
          .regex(/^\d+$/, "Must be a number")
          .refine((val) => !val || validFreightClasses.has(val), "Invalid Freight Class")
          .nullable()
      ),
      packagingType: z.string().min(1, "Standard Packaging is required"),
      packageUsWeight: numberSchemas.requiredPositiveDecimalString({ fieldName: "Package UoM Quantity" }),
      uoms: z.object({
        us: z.string(),
        metric: z.string(),
      }),
      packageMetricWeight: numberSchemas.requiredPositiveDecimalString({ fieldName: "Package UoM Quantity" }),
      packageLength: numberSchemas.optionalPositiveDecimalString(),
      packageWidth: numberSchemas.optionalPositiveDecimalString(),
      packageHeight: numberSchemas.optionalPositiveDecimalString(),
      palletUsWeight: numberSchemas.requiredPositiveDecimalString({ fieldName: "Pallet UoM Quantity" }),
      palletMetricWeight: numberSchemas.requiredPositiveDecimalString({ fieldName: "Pallet UoM Quantity" }),
      palletSizeLength: numberSchemas.optionalPositiveDecimalString(),
      palletSizeWidth: numberSchemas.optionalPositiveDecimalString(),
      palletSizeHeight: numberSchemas.optionalPositiveDecimalString(),
      palletSizeUom: z.string().default(INCH_UOM_ID),
      packagesPerPallet: numberSchemas.optionalPositiveIntegerString(),
      form: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const hasFreightItem = data.freightItem !== null && data.freightItem !== ""
      const hasFreightClass = data.freightClass !== null && data.freightClass !== ""

      // Both must be empty/null or both must be filled with valid values
      if (!((!hasFreightItem && !hasFreightClass) || (hasFreightItem && hasFreightClass))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Both Freight Item and Freight Class must be provided together",
          path: ["form"],
        })
        return
      }

      // Skip validation if either field is empty
      if (!data.freightItem || !data.freightClass) return

      // Check if the pair exists in our valid pairs
      const key = `${data.freightItem}-${data.freightClass}`
      if (!freightPairs.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid Freight Item and Class combination",
          path: ["form"],
        })
      }
    })
}

export type PackagingInfoFormValues = z.infer<ReturnType<typeof createPackagingInfoSchema>>

export const PACKAGING_REQUIRED_FIELDS: (keyof PackagingInfoFormValues)[] = [
  "packagingType",
  "packageUsWeight",
  "packageMetricWeight",
  "palletUsWeight",
  "palletMetricWeight",
]
